import subscriptionApi from 'api/subscription';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { Button, Header, Loader } from 'components';
import { useCommunity } from "components/CommunityContext";
import MembershipDatePicker from "components/MembershipDatePicker";
import getSymbolFromCurrency from "currency-symbol-map";
import { getReccuringLabel } from 'helpers/subscriptions';
import { Status } from 'models/member';
import { SubscriptionDetailsDto, SubscriptionPriceDto } from 'models/subscriptions';
import moment from "moment";
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { profileActions } from 'redux/reducers/profile';
import { selectProfile } from 'redux/selectors/profile';

const SubscriptionPlan = (): JSX.Element => {
  const [availablePlans, setAvailablePlans] = useState<SubscriptionDetailsDto[]>([]);
  const [selectedSubscription, setSelectedSubscription] = useState<SubscriptionDetailsDto | null>(null);
  const [selectedPrice, setSelectedPrice] = useState<SubscriptionPriceDto | null>(null);
  const [membershipStartDate, setMembershipStartDate] = useState<any>(new Date());
  const [isLoading, setIsLoading] = useState(false);
  const [currentRecurringIndex, setCurrentRecurringIndex] = useState(0);
  const [errorText, setErrorText] = useState('');
  const { texts: { membershipPage } } = useCommunity();
  const user = useAppSelector(selectProfile);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    if (isLoading || availablePlans?.length > 0) return;
    getAvailablePlans();
  }, []);

  useEffect(() => {
    if (user?.status !== Status.APPROVED) {
      navigate('/');
    }
    if (user?.profile?.membershipPreference?.startDate) {
      setMembershipStartDate(moment(user?.profile?.membershipPreference?.startDate).utc(true).toDate());
    }
  }, [user, navigate]);

  useEffect(() => {
    if (selectedSubscription && selectedPrice) {
      const priceIndex = selectedSubscription.prices.findIndex(price => price.id === selectedPrice.id);
      if (priceIndex !== -1) {
        setCurrentRecurringIndex(priceIndex);
      } else {
        setCurrentRecurringIndex(0);
      }
    }
  }, [selectedPrice, selectedSubscription]);

  useEffect(() => {
    if (availablePlans.length === 1 && !selectedSubscription) {
      const subscription = availablePlans[0];
      setSelectedSubscription(subscription);
      
      if (subscription.prices.length === 1 && !selectedPrice) {
        setSelectedPrice(subscription.prices[0]);
      }
    }
  }, [availablePlans, selectedSubscription, selectedPrice]);

  const handleSubmit = async () => {
    if (!selectedSubscription) {
      setErrorText('Please select a membership plan');
      return;
    }
    if (!selectedPrice) {
      setErrorText('Please select a membership plan');
      return;
    }
    if (!membershipStartDate) {
      setErrorText('Please select a membership start date');
      return;
    }

    const { url } = await subscriptionApi.createSubscription(selectedPrice.id);
    if (!url) return;
    await dispatch(profileActions.getProfile());
    window.location.href = url;
  };

  const getAvailablePlans = async () => {
    try {
      setIsLoading(true);
      const { subscriptions } = await subscriptionApi.getSubscriptions();
      if (subscriptions?.length) {
        let filteredSubscriptions = subscriptions;
        
        // Filter by subscription ID if locked
        if (user?.profile?.membershipPreference?.subscriptionId && 
            user?.profile?.membershipPreference?.subscriptionId !== 'None' && 
            user?.profile?.membershipPreference?.isSubscriptionLocked) {
          const lockedSubscription = subscriptions.find(x => x.id === user?.profile?.membershipPreference?.subscriptionId);
          if (lockedSubscription){
            lockedSubscription.prices = lockedSubscription.onboardingAvailableIntervals?.length > 0
              ? lockedSubscription.prices.filter(price => lockedSubscription.onboardingAvailableIntervals?.includes(price.reccuring))
              : lockedSubscription.prices;
            filteredSubscriptions = [lockedSubscription]
          }
        } else {
          filteredSubscriptions = subscriptions.filter(x => !x.isArchived);
        }

        // Filter prices if price is locked
        if (user?.profile?.membershipPreference?.priceId && 
            user?.profile?.membershipPreference?.isPriceLocked) {
          filteredSubscriptions = filteredSubscriptions.map(subscription => ({
            ...subscription,
            prices: subscription.prices.filter(price => price.id === user?.profile?.membershipPreference?.priceId)
          })).filter(subscription => subscription.prices.length > 0);
        }

        // Filter prices based on onboardingAvailableIntervals if not locked
        if (!user?.profile?.membershipPreference?.isSubscriptionLocked && !user?.profile?.membershipPreference?.isPriceLocked) {
          filteredSubscriptions = filteredSubscriptions.map(subscription => ({
            ...subscription,
            prices: subscription.onboardingAvailableIntervals?.length > 0
              ? subscription.prices.filter(price => subscription.onboardingAvailableIntervals?.includes(price.reccuring))
              : subscription.prices
          })).filter(subscription => subscription.prices.length > 0);
        }

        setAvailablePlans(filteredSubscriptions);
        
        // Set initial subscription and price
        if (user?.profile?.membershipPreference?.subscriptionId && 
            user?.profile?.membershipPreference?.subscriptionId !== 'None') {
          const initialSubscription = filteredSubscriptions.find(x => x.id === user?.profile?.membershipPreference?.subscriptionId);
          if (initialSubscription) {
            setSelectedSubscription(initialSubscription);
            if (user?.profile?.membershipPreference?.priceId) {
              const initialPrice = initialSubscription.prices.find(p => p.id === user?.profile?.membershipPreference?.priceId);
              if (initialPrice) {
                setSelectedPrice(initialPrice);
              }
            }
          }
        } else {
          const defaultSubscription = filteredSubscriptions.find(x => x.isDefault) || filteredSubscriptions[0];
          setSelectedSubscription(defaultSubscription);
          if (defaultSubscription?.prices?.length) {
            setSelectedPrice(defaultSubscription.prices[0]);
          }
        }
      }
    } catch (error) {
      console.log("Can't get available subscription plans", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleToggle = () => {
    if (!selectedSubscription) return;
    
    const currentIndex = selectedSubscription.prices.findIndex(price => price.id === selectedPrice?.id);
    const nextIndex = (currentIndex + 1) % selectedSubscription.prices.length;
    setSelectedPrice(selectedSubscription.prices[nextIndex]);
    setCurrentRecurringIndex(nextIndex);
  };

  const handlePeriodChange = (price: SubscriptionPriceDto) => {
    setSelectedPrice(price);
  };

  const getPeriodLabel = (price: SubscriptionPriceDto) => {
      return price.reccuring
      ? `Per ${getReccuringLabel(price.reccuring).toLowerCase()}`
      : '';
    };

  return (
    <>
      <Header onBack={() => navigate(-1)} withBackArrow={false} />
      {isLoading ? <Loader /> : 
        availablePlans.length === 0 ? (
          <div className="subscriptions-empty-container">
            <h2 className='subscriptions-empty-text'>Check back soon. We're busy crafting our membership offer and will be in touch as soon as it's ready</h2>
          </div>
        ) :
      <>
        <div className="subscription-plan-page">
          <div className="subscription-price-block">
            {selectedPrice && <div className="membership-price-block">
              <div className="membership-price-row">
                <div className="membership-price-col">
                  <div className="membership-price">
                    {selectedPrice ? `${getSymbolFromCurrency(selectedPrice?.currency)} ${selectedPrice.amount / 100}` : '-'}
                  </div>
                  <div className="membership-per-month">{getPeriodLabel(selectedPrice).toUpperCase()}</div>
                </div>
                {selectedSubscription?.prices && selectedSubscription.prices.length === 2 && selectedSubscription.prices[0] && selectedSubscription.prices[1] && (
                  <div className="membership-toggle-row">
                    <span className="membership-toggle-label">
                      {getReccuringLabel(selectedSubscription.prices[0].reccuring)}
                    </span>
                    <span
                      className="membership-toggle"
                      onClick={handleToggle}
                    >
                      <span
                        className="membership-toggle-circle"
                        style={{ left: currentRecurringIndex === 0 ? 2 : 17 }}
                      />
                    </span>
                    <span className="membership-toggle-label">
                      {getReccuringLabel(selectedSubscription.prices[1].reccuring)}
                    </span>
                  </div>
                )}
                {selectedSubscription && selectedSubscription?.prices?.length > 2 && selectedSubscription?.prices && (
                  <div className="membership-multi-toggle-row">
                    {selectedSubscription.prices.map((price, idx) => (
                      <button
                        key={`${price.id}-${idx}`}
                        className={
                          idx === currentRecurringIndex
                            ? 'membership-multi-toggle-btn active'
                            : 'membership-multi-toggle-btn'
                        }
                        onClick={() => {
                          setCurrentRecurringIndex(idx);
                          handlePeriodChange(price);
                        }}
                        type="button"
                      >
                        {getReccuringLabel(price.reccuring)}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <div className="membership-onboarding-asterisk-container">
                {selectedSubscription?.joiningFee &&
                  <div className="membership-onboarding-fee">+ {getSymbolFromCurrency(selectedPrice?.currency)}{selectedSubscription?.joiningFee} one-time onboarding fee</div>
                }
              </div>
            </div>}
            <MembershipDatePicker label="Membership start date" value={membershipStartDate} onChange={setMembershipStartDate} disabled={!!user?.profile?.membershipPreference?.startDate} />
          </div>
          <div className="membership-wrapper">
            <div className="membership-choices">
              {availablePlans.map((option) => (
                <div
                  key={option.id}
                  className={selectedSubscription?.id === option.id ? 'membership-choice membership-choice-selected' : 'membership-choice'}
                  onClick={() => {
                    setSelectedSubscription(option);
                    setSelectedPrice(option.prices[0]);
                  }}
                >
                  <div className="membership-title-container">
                    <span className="membership-radio">
                      <span className={`membership-radio-dot ${selectedSubscription?.id === option.id ? 'active' : ''}`} />
                    </span>
                    <span className="membership-choice-text">{option.name}</span>
                  </div>
                  <div className="membership-choice-content">
                    {selectedSubscription?.id === option.id &&
                      <>
                        <div
                          className="membership-description"
                          dangerouslySetInnerHTML={{ __html: option.description }}
                        />
                        {option?.benefitsLink && <div className="membership-description-link">
                          <a href={option.benefitsLink} target="_blank" rel="noopener noreferrer">
                            {membershipPage?.benefitsLink || 'See details here'}
                          </a>
                        </div>}
                      </>
                    }
                  </div>
                </div>
              ))}
            </div>
            {errorText && <div className="membership-date-error">{errorText}</div>}
          </div>
          {selectedSubscription?.features && selectedSubscription.features.length > 0 && (
            <div className="subscription-plan-details-block">
              <span className="membership-choice-text">{selectedSubscription.name}</span>
              <ul className="membership-feature-row">
                {selectedSubscription.features.map((feature: any, index: number) => (
                  <li key={index} className="membership-feature-text">{feature}</li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* Mobile */}
        <div className="subscription-plan-page-mobile">
          <div className="membership-wrapper">
            <div className="membership-choices">
              {availablePlans.map((option) => (
                <div
                  key={option.id}
                  className={selectedSubscription?.id === option.id ? 'membership-choice membership-choice-selected' : 'membership-choice'}
                  onClick={() => {
                    setSelectedSubscription(option);
                    setSelectedPrice(option.prices[0]);
                  }}
                >
                  <div className="membership-title-container">
                    <span className="membership-radio">
                      <span className={`membership-radio-dot ${selectedSubscription?.id === option.id ? 'active' : ''}`} />
                    </span>
                    <span className="membership-choice-text">{option.name}</span>
                  </div>
                  <div className="membership-choice-content">
                    {selectedSubscription?.id === option.id &&
                      <>
                        <div
                          className="membership-description"
                          dangerouslySetInnerHTML={{ __html: option.description }}
                        />
                        {option?.benefitsLink && <div className="membership-description-link">
                          <a href={option.benefitsLink} target="_blank" rel="noopener noreferrer">
                            {membershipPage?.benefitsLink || 'See details here'}
                          </a>
                        </div>}
                      </>
                    }
                  </div>
                </div>
              ))}
            </div>
            {selectedPrice && <div className="membership-price-block">
              <div className="membership-price-row">
                <div className="membership-price-col">
                  <div className="membership-price">
                    {selectedPrice ? `${getSymbolFromCurrency(selectedPrice?.currency)} ${selectedPrice.amount / 100}` : '-'}
                  </div>
                  <div className="membership-per-month">{getPeriodLabel(selectedPrice)}</div>
                </div>
                {selectedSubscription?.prices && selectedSubscription.prices.length === 2 && selectedSubscription.prices[0] && selectedSubscription.prices[1] && (
                  <div className="membership-toggle-row">
                    <span className="membership-toggle-label">
                      {getPeriodLabel(selectedSubscription.prices[0])}
                    </span>
                    <span
                      className="membership-toggle"
                      onClick={handleToggle}
                    >
                      <span
                        className="membership-toggle-circle"
                        style={{ left: currentRecurringIndex === 0 ? 2 : 17 }}
                      />
                    </span>
                    <span className="membership-toggle-label">
                      {getPeriodLabel(selectedSubscription.prices[1])}
                    </span>
                  </div>
                )}
                {selectedSubscription && selectedSubscription?.prices?.length > 2 && selectedSubscription?.prices && (
                  <div className="membership-multi-toggle-row">
                    {selectedSubscription.prices.map((price, idx) => (
                      <button
                        key={`${price.id}-${idx}`}
                        className={
                          idx === currentRecurringIndex
                            ? 'membership-multi-toggle-btn active'
                            : 'membership-multi-toggle-btn'
                        }
                        onClick={() => {
                          setCurrentRecurringIndex(idx);
                          handlePeriodChange(price);
                        }}
                        type="button"
                      >
                        {getPeriodLabel(price)}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <div className="membership-onboarding-asterisk-container">
                {selectedSubscription?.joiningFee &&
                  <div className="membership-onboarding-fee">+ {getSymbolFromCurrency(selectedPrice?.currency)}{selectedSubscription?.joiningFee} one-time onboarding fee</div>
                }
              </div>
            </div>}
            <MembershipDatePicker label="Membership start date" value={membershipStartDate} onChange={setMembershipStartDate} disabled={!!user?.profile?.membershipPreference?.startDate} />
            {errorText && <div className="membership-date-error">{errorText}</div>}
          </div>
        </div>
        <div className="subscription-plan-page-footer">
          <Button text="Continue" type="button" onClick={handleSubmit} />
        </div>
      </>}
    </>
  );
};

export default SubscriptionPlan;
